import React, { useState, useEffect } from 'react';
import { PAIEMENT_ACTIF } from '../config/features';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '../i18n/router';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import { API_BASE_URL } from '../config/api';
interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
  titleDisplay?: string;
}

const FormCoteIvoire: React.FC = () => {
  const { t } = useTranslation('formulaires');
  const { t: tp } = useTranslation('formulaires_pays');
  // Récupération des paramètres de route
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const titleDisplay = (state as any)?.titleDisplay || (state as any)?.title || '';
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // Champs provenant de la navigation ou valeurs par défaut
  const [title, setTitle] = useState<string>(state?.title || '');
  const [prixconsulat, setPrixconsulat] = useState<number>(state?.prixconsulat || 0);
  const [prixintervention, setPrixintervention] = useState<number>(state?.prixintervention || 0);
  const [urlFlag, setUrlFlag] = useState<string>(state?.url_flag || '');
  const [validite, setValidite] = useState<string>(state?.validite || '');
  const commentaires = state?.commentaires || '';
  const [informations, setInformations] = useState<string>(state?.informations || '');

  // Champs du formulaire
  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');
  const [voyageurs, setVoyageurs] = useState<any[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFees] = useState<boolean>(false);

  // Etats pour la gestion du type d'utilisateur et l'ecran de succes
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // Gestion des uploads de documents
  const [letterInvitationFile, setLetterInvitationFile] = useState<File | null>(null);
  const [billetAllerRetourFile, setBilletAllerRetourFile] = useState<File | null>(null);
  const [copieReservationHotelFile, setCopieReservationHotelFile] = useState<File | null>(null);

  // Optionnel : prévisualisation des fichiers uploadés
  const [letterInvitationPreview, setLetterInvitationPreview] = useState<string | null>(null);
  const [billetAllerRetourPreview, setBilletAllerRetourPreview] = useState<string | null>(null);
  const [copieReservationHotelPreview, setCopieReservationHotelPreview] = useState<string | null>(null);

  // Calcul du total
  const total = prixconsulat + prixintervention + (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0);

  // Fonction de validation du formulaire
  const validateForm = () => {
    const parseDate = (dateStr: string): Date | null => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return null;
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);

    const errors: string[] = [];

    if (departDate) {
      if (isNaN(departDate.getTime()) || departDate <= today) {
        errors.push(tp('coteIvoire.errors.departureDateInvalid'));
      }
    } else {
      errors.push(tp('coteIvoire.errors.departureDateRequired'));
    }

    if (dateSortie && sortieDate) {
      if (isNaN(sortieDate.getTime()) || (departDate && sortieDate <= departDate)) {
        errors.push(tp('coteIvoire.errors.returnDateInvalid'));
      }
    } else if (dateSortie && !sortieDate) {
      errors.push(tp('coteIvoire.errors.returnDateRequired'));
    }

    if (!selectedVoyageur) errors.push(t('common.validation.travelerNotSelected'));
    if (!dateDepart.trim()) errors.push(t('common.validation.departureDate'));
    if (!dateSortie.trim()) errors.push(t('common.validation.returnDate'));

    // Pour un visa affaire, la lettre d'invitation est obligatoire
    if (title.toLowerCase().includes('affaire') && !letterInvitationFile) {
      errors.push(tp('coteIvoire.validation.lettreInvitation'));
    }
    if (!billetAllerRetourFile) {
      errors.push(tp('coteIvoire.validation.billetAllerRetour'));
    }
    if (!copieReservationHotelFile) {
      errors.push(tp('coteIvoire.validation.copieReservationHotel'));
    }
    if (!assuranceVisa.trim()) errors.push(t('common.validation.insurance'));
    if (!sliderValue) errors.push(t('common.validation.cguAcceptance'));

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateDepart,
    dateSortie,
    selectedVoyageur,
    letterInvitationFile,
    billetAllerRetourFile,
    copieReservationHotelFile,
    assuranceVisa,
    sliderValue,
  ]);

  // Récupération de la liste des voyageurs depuis le localStorage
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Verification si l'utilisateur appartient a une societe
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la verification du statut utilisateur:', error);
        setIsClientSociete(true); // Par defaut, considerer comme societe
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find((v: any) => v.id.toString() === selectedVoyageur);

  // Empêcher la fermeture accidentelle de l’onglet
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Handlers pour l'upload des fichiers
  const handleLetterInvitationUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setLetterInvitationFile(file);
      const fileUrl = URL.createObjectURL(file);
      setLetterInvitationPreview(fileUrl);
    }
  };

  const handleBilletUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setBilletAllerRetourFile(file);
      const fileUrl = URL.createObjectURL(file);
      setBilletAllerRetourPreview(fileUrl);
    }
  };

  const handleCopieUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setCopieReservationHotelFile(file);
      const fileUrl = URL.createObjectURL(file);
      setCopieReservationHotelPreview(fileUrl);
    }
  };

  // Formatage de la date au format jj/mm/aaaa
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  const handleBack = () => {
    if (window.confirm(t('common.confirmation.unsavedChanges'))) {
      navigate(-1);
    }
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId: string | null = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }
      const etatFormulaire = champsNonRemplis.length === 0;
      const formData = new FormData();
      formData.append('etatFormulaire', String(etatFormulaire));
      formData.append('idUtilisateur', userId || '');
      formData.append('idVoyageur', selectedVoyageur);
      formData.append('titreVisa', title);
      formData.append('dateDepart', dateDepart);
      formData.append('dateSortie', dateSortie);
      // Ajout des fichiers
      if (title.toLowerCase().includes('affaire') && letterInvitationFile) {
        formData.append('lettreInvitation', letterInvitationFile);
      }
      if (billetAllerRetourFile) {
        formData.append('billetAllerRetour', billetAllerRetourFile);
      }
      if (copieReservationHotelFile) {
        formData.append('copieReservationHotel', copieReservationHotelFile);
      }
      formData.append('assuranceVisa', assuranceVisa);
      formData.append('prixconsulat', String(prixconsulat));
      formData.append('prixintervention', String(prixintervention));
      formData.append('validite', validite);

      const response = await axios.post(`${API_BASE_URL}/validerDemande`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setIsLoading(false);

      // Stocker l'ID de la demande creee si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'ecran de succes au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Erreur lors de la validation de la demande:', error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors societe)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Creer une nouvelle demande (reste sur le meme formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setLetterInvitationFile(null);
    setBilletAllerRetourFile(null);
    setCopieReservationHotelFile(null);
    setLetterInvitationPreview(null);
    setBilletAllerRetourPreview(null);
    setCopieReservationHotelPreview(null);
  };

  const disabledPressHandler = () => {};

  // Si l'ecran de succes est affiche
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tete avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succes */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              {tp('coteIvoire.success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {tp('coteIvoire.success.message', { title: titleDisplay })}
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors societe) */}
              {isClientSociete === false && !hideFees && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  {PAIEMENT_ACTIF ? tp('coteIvoire.success.payOnline') : t('success.launchDemande')}
                </Button>
              )}

              {/* Bouton Creer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                {tp('coteIvoire.success.newRequest')}
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                {tp('coteIvoire.success.viewRequests')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
    >
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              {t('common.formTitle', { title: titleDisplay })}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur si non en mode édition */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">{t('common.selectTraveler')}</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label={t('common.selectTraveler')}
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map((v: any) => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? t('common.selectTravelerWithPassport', { passport: v.numeropax }) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#D89D2F',
                    borderColor: '#D89D2F',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#B5811F'
                    }
                  }}
                >
                  {t('common.createNewTraveler')}
                </Button>
              </Box>
            </>
          )}

          {/* Avatar du voyageur sélectionné */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt={t('common.alt.avatar')}
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          <TextField
            label={t('common.departureDate.label')}
            placeholder={t('common.departureDate.placeholder')}
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('common.returnDate.label')}
            placeholder={t('common.returnDate.placeholder')}
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Upload de la lettre d'invitation si visa affaire */}
          {title.toLowerCase().includes('affaire') && (
            <Box sx={{ my: 2 }}>
              <Typography variant="body1" gutterBottom>
                {tp('coteIvoire.uploads.lettreInvitationLabel')}
              </Typography>
              <input type="file" accept="image/*,application/pdf" onChange={handleLetterInvitationUpload} />
              {letterInvitationPreview && (
                <Box sx={{ mt: 2 }}>
                  <img
                    src={letterInvitationPreview}
                    alt={tp('coteIvoire.uploads.lettreInvitationAlt')}
                    style={{ maxWidth: '100%', maxHeight: 300 }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Upload du billet aller-retour */}
          <Box sx={{ my: 2 }}>
            <Typography variant="body1" gutterBottom>
              {tp('coteIvoire.uploads.billetAllerRetourLabel')}
            </Typography>
            <input type="file" accept="image/*,application/pdf" onChange={handleBilletUpload} />
            {billetAllerRetourPreview && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={billetAllerRetourPreview}
                  alt={tp('coteIvoire.uploads.billetAllerRetourAlt')}
                  style={{ maxWidth: '100%', maxHeight: 300 }}
                />
              </Box>
            )}
          </Box>

          {/* Upload de la copie de réservation hôtel / certificat d’hébergement */}
          <Box sx={{ my: 2 }}>
            <Typography variant="body1" gutterBottom>
              {tp('coteIvoire.uploads.copieReservationHotelLabel')}
            </Typography>
            <input type="file" accept="image/*,application/pdf" onChange={handleCopieUpload} />
            {copieReservationHotelPreview && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={copieReservationHotelPreview}
                  alt={tp('coteIvoire.uploads.copieReservationHotelAlt')}
                  style={{ maxWidth: '100%', maxHeight: 300 }}
                />
              </Box>
            )}
          </Box>

          {/* Module Assurance Visa */}
          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>

          {/* Récapitulatif des frais */}
          {!hideFees && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">{t('common.fees.visa', { prix: prixconsulat })}</Typography>
              <Typography variant="body1">{t('common.fees.service', { prix: prixintervention })}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {t('common.fees.total', { total })}
              </Typography>
            </Box>
          )}

          {/* Affichage de l'état du formulaire */}
          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  {t('common.formStatus.incomplete')}
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">{t('common.formStatus.complete')}</Typography>
            )}
          </Box>

          {/* Bouton de validation */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                {t('common.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormCoteIvoire;
